/*******************************************************
 * Copyright (C) BoostCommerce.net
 *
 * This file is part of commercial BoostCommerce.net projects.
 *
 * This file can not be copied and/or distributed without the express
 * permission of BoostCommerce.net
 *
 * @Date:   2024-07-105 10:04:45
 *
 *******************************************************/
// @flow

import _ from 'lodash';
import { SubmissionError } from 'redux-form';
import { BUNDLE_DEFAULT_DESIGN_SETTINGS } from 'src/constants/bundle';
import type { PredictiveBundleWidgetType } from 'src/models/bundle';

/**
 * Build initial dynamic bundle widget
 *
 * @param  {PredictiveBundleWidgetType} widget widget
 * @return {object}
 */
export const buildInitialWidgetValues = (widget: PredictiveBundleWidgetType) => {
  return {
    ...widget,
    recommendationType: _.get(widget, 'recommendationType', 'dynamic-bundle'),
    bundleStatus: _.get(widget, 'bundleStatus', 'active'),
    bundleProducts: _.get(widget, 'bundleProducts', []),
    discountType: _.get(widget, 'discountType', 'none'),
    discountValue: _.get(widget, 'discountValue', 0),
    placement: _.get(widget, 'placement', ['instant-search-widget', 'search-result-page'])
  };
};

/**
 * Validate general settings of widget rule
 *
 * @param  {RecommendationWidgetType} recommendation widget
 * @return {boolean}
 */
export const validateFromGeneral = (data, i18n) => {
  const { bundleProducts, discountType, discountValue, placement } = data;

  if (_.isEmpty(bundleProducts)) {
    throw new SubmissionError({
      bundleProducts: i18n.translate(
        'AdminHTML.PredictiveBundle.Validate.invalid_BundleProducts_Empty'
      )
    });
  }
  if (discountType !== 'none') {
    if (discountValue === '') {
      throw new SubmissionError({
        discountValue: i18n.translate(
          'AdminHTML.PredictiveBundle.Validate.invalid_DiscountValue_Empty'
        )
      });
    }

    if (Number(discountValue) <= 0) {
      throw new SubmissionError({
        discountValue: i18n.translate('AdminHTML.PredictiveBundle.Validate.invalid_DiscountValue_Min')
      });
    }
    if (discountType === 'percentage') {
      if (Number(discountValue) > 100) {
        throw new SubmissionError({
          discountValue: i18n.translate(
            'AdminHTML.PredictiveBundle.Validate.invalid_DiscountValue_Max'
          )
        });
      }
    }
  }

  if (_.isEmpty(placement)) {
    throw new SubmissionError({
      placement: i18n.translate('AdminHTML.PredictiveBundle.Validate.invalid_Placement_Empty')
    });
  }
  return true;
};

/**
 * Build dynamic bundle general settings
 *
 * @param  {PredictiveBundleWidgetType} data widget
 * @return {object}
 */
export const buildWidgetValues = (data: PredictiveBundleWidgetType) => {
  const { discountValue } = data;
  delete data.widgetDesignSettings;

  return {
    ...data,
    discountValue: Number(discountValue)
  };
};

/**
 * Build initial dynamic bundle widget settings
 *
 * @param  {PredictiveBundleWidgetType} widget widget
 * @return {object}
 */
export const buildInitialDesignSettingsValues = (
  widget: PredictiveBundleWidgetType,
  defaultDesignSettings: Object
) => {
  const widgetDesignSettings = _.get(widget, 'widgetDesignSettings', {});
  // Fonts
  const widgetTitleFont = initFonts(widgetDesignSettings, 'titleFont');
  const {
    templateType = BUNDLE_DEFAULT_DESIGN_SETTINGS.templateType,
    titleName = BUNDLE_DEFAULT_DESIGN_SETTINGS.titleName,
    titleAlignment = BUNDLE_DEFAULT_DESIGN_SETTINGS.titleAlignment,
    titleTextColor = BUNDLE_DEFAULT_DESIGN_SETTINGS.titleTextColor,
    titleFont = widgetTitleFont.titleFont,
    titleTextTransform = BUNDLE_DEFAULT_DESIGN_SETTINGS.titleTextTransform,
    titleFontStyle = widgetTitleFont.titleFontStyle,
    titleFontSize = BUNDLE_DEFAULT_DESIGN_SETTINGS.titleFontSize,
    buttonText = BUNDLE_DEFAULT_DESIGN_SETTINGS.buttonText,
    buttonBackgroundColor = BUNDLE_DEFAULT_DESIGN_SETTINGS.buttonBackgroundColor,
    buttonTextColor = BUNDLE_DEFAULT_DESIGN_SETTINGS.buttonTextColor
  } = defaultDesignSettings;

  const defaultSetting = {
    templateType: _.get(widgetDesignSettings, 'templateType', templateType),
    titleName: _.get(widgetDesignSettings, 'titleName', titleName),
    titleAlignment: _.get(widgetDesignSettings, 'titleAlignment', titleAlignment),
    titleTextColor: _.get(widgetDesignSettings, 'titleTextColor', titleTextColor),
    titleFont,
    titleTextTransform: _.get(widgetDesignSettings, 'titleTextTransform', titleTextTransform),
    titleFontStyle,
    titleFontSize: _.get(widgetDesignSettings, 'titleFontSize', titleFontSize),
    buttonText: _.get(widgetDesignSettings, 'buttonText', buttonText),
    buttonBackgroundColor: _.get(
      widgetDesignSettings,
      'buttonBackgroundColor',
      buttonBackgroundColor
    ),
    buttonTextColor: _.get(widgetDesignSettings, 'buttonTextColor', buttonTextColor),
    isDefault: widgetDesignSettings.templateType === 'default'
  };

  return defaultSetting;
};

/**
 * Build dynamic bundle settings
 *
 * @param  {PredictiveBundleWidgetType} data widget
 * @return {object}
 */
export const buildDesignSettingsValues = (
  widget: PredictiveBundleWidgetType,
  data: PredictiveBundleWidgetType,
  isForAPI = false
) => {
  const widgetDesignSettings = {
    widgetId: widget.id,
    tenantId: widget.tenantId,
    templateType: data.isDefault ? 'default' : 'customization',
    titleName: data.titleName || '',
    titleAlignment: data.titleAlignment || '',
    titleFont: data.titleFont || '',
    titleFontSize: data.titleFontSize || '',
    titleFontStyle: data.titleFontStyle || '',
    titleTextColor: data.titleTextColor || '',
    titleTextTransform: data.titleTextTransform || '',
    buttonText: data.buttonText || '',
    buttonBackgroundColor: data.buttonBackgroundColor || '',
    buttonTextColor: data.buttonTextColor || ''
  };

  const widgetSettings = {
    ...widget,
    widgetDesignSettings,
    widgetName: data.titleName || ''
  };

  delete widgetSettings._id;

  if (isForAPI) {
    const { bundleProducts } = widget;
    widgetSettings.bundleProducts = _.map(bundleProducts, product => product.id);
  }
  return widgetSettings;
};

/**
 * Init fonts
 * @param {*} settings
 * @param {*} fontField
 * @returns
 */
const initFonts = (settings, fontField) => {
  const font = _.get(settings, fontField, {});
  if (_.isEmpty(font))
    return {
      titleFont: BUNDLE_DEFAULT_DESIGN_SETTINGS.titleFont,
      titleFontStyle: BUNDLE_DEFAULT_DESIGN_SETTINGS.titleFontStyle
    };

  const { titleFont, titleFontStyle = '' } = settings;
  return {
    titleFont,
    titleFontStyle
  };
};
